import * as THREE from 'three';
import type { ExplosionClass } from '../../game/explosions';
import type { LightPool } from '../LightPool';
import { EXPLOSION } from '../palette';
import { Layer } from '../RenderLayers';
import { bakeExplosionSheet, type Flipbook } from './flipbook';
import { SparkField } from './SparkField';

/* ═══════════════════════════════════════════════════════════════════════════
   L'orchestration des explosions — trois couches, une seule entrée.

   Couche 1 — le CŒUR turbulent : une planche (flipbook) échantillonnée sur un
   quad additif. La planche n'est pas sourcée mais CUITE au boot par
   `bakeExplosionSheet` (une boule de feu fbm procédurale, voir `flipbook.ts`) —
   les planches d'explosion n'existant en CC0 nulle part, on les génère, comme
   les icônes PWA et les silhouettes de vaisseau. Le quad ne fait qu'interpoler
   entre deux images : zéro simulation, un échantillonnage. Remplacer la planche
   par un vrai rendu volumétrique ne toucherait QUE `flipbook.ts` — le cœur, lui,
   ignore d'où vient la texture.

   Couche 2 — débris et étincelles : trajectoire analytique en vertex shader.
   Couche 3 — lumière, trauma (et l'onde de choc, portée par le pipeline écran).
   ═══════════════════════════════════════════════════════════════════════════ */

const CORE_CAPACITY = 24;
/** Planche courante : grille 8×8 (64 images), 128 px par image → texture 1024².
 *  VRAM = 1024² × 4 = 4 Mo (RGBA8, sans mip). Mesuré et exposé (`vramBytes`)
 *  pour le budget du jalon J5. Une planche 4×4 dédiée aux boss viendra au J9. */
const SHEET_GRID = 8;
const SHEET_FRAME_PX = 128;

const CORE_VERT = /* glsl */ `
attribute vec3 iCenter;
attribute float iBirth;
attribute float iLife;
attribute float iRadius;
attribute float iScrollBirth;

uniform float uTime;
uniform float uScroll;

varying float vAge;
varying vec2 vLocal;

void main() {
  float t = uTime - iBirth;
  float alive = step(0.0, t) * step(t, iLife);
  vAge = clamp(t / iLife, 0.0, 1.0);
  vLocal = uv;

  // Expansion rapide puis stabilisation — une explosion gonfle d'un coup, elle
  // ne grandit pas linéairement. La racine donne cette attaque franche.
  float grow = sqrt(vAge);
  float r = iRadius * (0.25 + 0.75 * grow) * alive;

  vec3 p = iCenter;
  p.y -= (uScroll - iScrollBirth);

  vec4 mv = modelViewMatrix * vec4(p, 1.0);
  mv.xy += position.xy * r * 2.0;
  gl_Position = projectionMatrix * mv;
}
`;

const CORE_FRAG = /* glsl */ `
precision highp float;
varying float vAge;
varying vec2 vLocal;

uniform sampler2D uSheet;
uniform float uGrid;
uniform float uFrames;

// Échantillonne l'image \`frame\` de la planche à la coordonnée locale donnée.
vec4 sampleFrame(float frame, vec2 local) {
  frame = clamp(frame, 0.0, uFrames - 1.0);
  float gx = mod(frame, uGrid);
  float gy = floor(frame / uGrid);
  // Léger retrait pour ne pas mordre la cellule voisine en filtrage bilinéaire.
  vec2 uv = (vec2(gx, gy) + clamp(local, 0.003, 0.997)) / uGrid;
  return texture2D(uSheet, uv);
}

void main() {
  // La planche encode une évolution CONTINUE : on interpole entre deux images
  // successives pour un billonnement fluide plutôt qu'un défilement saccadé.
  float fpos = clamp(vAge, 0.0, 1.0) * (uFrames - 1.0);
  float f0 = floor(fpos);
  vec4 a = sampleFrame(f0, vLocal);
  vec4 b = sampleFrame(f0 + 1.0, vLocal);
  vec4 tex = mix(a, b, fpos - f0);

  // Additif : la contribution ajoutée vaut rgb·alpha. La forme et le fondu sont
  // déjà cuits dans la planche ; on garde un petit gain HDR pour nourrir le bloom.
  gl_FragColor = vec4(tex.rgb * 1.15, tex.a);
}
`;

export class ExplosionFx {
  readonly sparks: SparkField;
  private readonly coreMesh: THREE.Mesh;
  private readonly geo: THREE.InstancedBufferGeometry;
  private readonly aCenter: THREE.InstancedBufferAttribute;
  private readonly aBirth: THREE.InstancedBufferAttribute;
  private readonly aLife: THREE.InstancedBufferAttribute;
  private readonly aRadius: THREE.InstancedBufferAttribute;
  private readonly aScroll: THREE.InstancedBufferAttribute;
  private readonly uniforms: {
    uTime: { value: number };
    uScroll: { value: number };
    uSheet: { value: THREE.Texture };
    uGrid: { value: number };
    uFrames: { value: number };
  };
  private head = 0;
  private dirty = false;
  private readonly flipbook: Flipbook;

  private readonly tint = new THREE.Color(EXPLOSION[0]);
  /** Teinte de la gerbe de bombe — cyan clair, palette d'interface (§4.5). */
  private readonly bombTint = new THREE.Color(0x9fe8ff);

  constructor(scene: THREE.Scene, renderer: THREE.WebGLRenderer, particleCapacity = 4096) {
    this.sparks = new SparkField({ capacity: particleCapacity, renderOrder: Layer.Vfx });
    scene.add(this.sparks.mesh);

    // La planche est cuite MAINTENANT, au boot, avant la boucle. Le programme de
    // cuisson est donc créé avant le relevé de référence du warm-up : il compte
    // dans la ligne de base, il ne sera jamais signalé comme « fuite ».
    this.flipbook = bakeExplosionSheet(renderer, {
      grid: SHEET_GRID,
      frameSize: SHEET_FRAME_PX,
      seed: 0.37,
    });

    const quad = new THREE.PlaneGeometry(1, 1);
    this.geo = new THREE.InstancedBufferGeometry();
    this.geo.index = quad.index;
    this.geo.attributes['position'] = quad.attributes['position']!;
    this.geo.attributes['uv'] = quad.attributes['uv']!;
    this.geo.instanceCount = CORE_CAPACITY;

    const inst = (size: number): THREE.InstancedBufferAttribute => {
      const a = new THREE.InstancedBufferAttribute(new Float32Array(CORE_CAPACITY * size), size);
      a.setUsage(THREE.DynamicDrawUsage);
      return a;
    };
    this.aCenter = inst(3);
    this.aBirth = inst(1);
    this.aLife = inst(1);
    this.aRadius = inst(1);
    this.aScroll = inst(1);

    this.geo.setAttribute('iCenter', this.aCenter);
    this.geo.setAttribute('iBirth', this.aBirth);
    this.geo.setAttribute('iLife', this.aLife);
    this.geo.setAttribute('iRadius', this.aRadius);
    this.geo.setAttribute('iScrollBirth', this.aScroll);

    this.uniforms = {
      uTime: { value: 0 },
      uScroll: { value: 0 },
      uSheet: { value: this.flipbook.texture },
      uGrid: { value: this.flipbook.grid },
      uFrames: { value: this.flipbook.frames },
    };

    const mat = new THREE.ShaderMaterial({
      vertexShader: CORE_VERT,
      fragmentShader: CORE_FRAG,
      uniforms: this.uniforms,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      blending: THREE.AdditiveBlending,
    });
    // Voir `SparkField` : la clé n'est pas nécessaire à la correction, elle
    // rend le programme identifiable dans le compte du warm-up. Bump v2 : le
    // cœur échantillonne désormais une planche au lieu d'un dégradé procédural.
    mat.customProgramCacheKey = (): string => 'explosion-core-v2';

    this.coreMesh = new THREE.Mesh(this.geo, mat);
    this.coreMesh.frustumCulled = false;
    this.coreMesh.renderOrder = Layer.Vfx + 1;
    scene.add(this.coreMesh);
  }

  /**
   * Déclenche une explosion complète.
   *
   * `lights` reçoit le pool à trois lampes : l'attribution se fait par
   * priorité, et un refus est silencieux — mieux vaut une explosion sans
   * lumière qu'un freeze de recompilation (PLAN.md §4.6).
   */
  burst(
    cls: ExplosionClass,
    x: number,
    y: number,
    now: number,
    scroll: number,
    rand: () => number,
    lights: LightPool | null,
  ): void {
    this.core(x, y, cls.coreRadius, cls.duration, now, scroll);

    this.sparks.burst(
      x,
      y,
      cls.particles,
      cls.speedMin,
      cls.speedMax,
      cls.duration * 0.9,
      6 + cls.coreRadius * 0.08,
      this.tint,
      now,
      scroll,
      rand,
    );

    if (lights && cls.lights > 0) {
      for (let i = 0; i < cls.lights; i++) {
        lights.flash(
          x,
          y,
          40,
          0xffb060,
          cls.lightIntensity,
          cls.lightRadius,
          cls.lightDuration,
          cls.priority,
        );
      }
    }
  }

  /** VRAM de la planche d'explosion, en octets. Exposée pour le budget mesuré
   *  du jalon J5 (le panneau de debug l'affiche). */
  get flipbookVram(): number {
    return this.flipbook.vramBytes;
  }

  /** Gerbe de la BOMBE — un anneau clair et rapide, cyan et non orange : la
   *  bombe est une action du joueur (elle efface le danger), pas une mort
   *  d'ennemi. La couleur la range du côté de l'interface, jamais du côté de ce
   *  qui tue (§4.5). L'onde de choc, elle, est déclenchée par l'appelant. */
  bombBurst(x: number, y: number, now: number, scroll: number, rand: () => number): void {
    this.sparks.burst(x, y, 48, 160, 620, 0.55, 9, this.bombTint, now, scroll, rand);
  }

  /** Couche 1 — le cœur : émet une image de planche à échantillonner. */
  private core(x: number, y: number, radius: number, life: number, now: number, scroll: number): void {
    const i = this.head;
    this.head = (this.head + 1) % CORE_CAPACITY;

    this.aCenter.array[i * 3] = x;
    this.aCenter.array[i * 3 + 1] = y;
    this.aCenter.array[i * 3 + 2] = 3;
    this.aBirth.array[i] = now;
    this.aLife.array[i] = life;
    this.aRadius.array[i] = radius;
    this.aScroll.array[i] = scroll;
    this.dirty = true;
  }

  /** Une frame. Aucun upload si rien n'a explosé. */
  update(now: number, scroll: number): void {
    this.uniforms.uTime.value = now;
    this.uniforms.uScroll.value = scroll;
    if (this.dirty) {
      for (const a of [this.aCenter, this.aBirth, this.aLife, this.aRadius, this.aScroll]) {
        a.needsUpdate = true;
      }
      this.dirty = false;
    }
    this.sparks.update(now, scroll);
  }
}
